// src/pages/student-portal/leaderboard/LeaderboardPage.js
// Student Portal Leaderboard (purple theme)

import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    Paper,
    Button,
    Chip,
    Skeleton,
    CircularProgress,
    Alert,
    Avatar,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import PersonIcon from '@mui/icons-material/Person';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import StarIcon from '@mui/icons-material/Star';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import SchoolIcon from '@mui/icons-material/School';
import MilitaryTechIcon from '@mui/icons-material/MilitaryTech';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import axiosInstance from '../../../utils/axiosInstance';

const fontFamily = "'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif";
const ACCENT = '#8b5cf6';

// ------------------------------------------------------------------
// Constants
// ------------------------------------------------------------------

const LEADERBOARD_TYPES = [
    { key: 'points', label: 'Points', icon: StarIcon },
    { key: 'courses_completed', label: 'Courses Completed', icon: SchoolIcon },
    { key: 'badges', label: 'Badges', icon: MilitaryTechIcon },
    { key: 'streak', label: 'Streak', icon: LocalFireDepartmentIcon },
];

const TIMEFRAMES = [
    { key: 'all_time', label: 'All Time' },
    { key: 'monthly', label: 'This Month' },
    { key: 'weekly', label: 'This Week' },
    { key: 'daily', label: 'Today' },
];

const MEDAL_COLORS = {
    gold: '#eab308',
    silver: '#9ca3af',
    bronze: '#d97706',
};

const RANK_CONFIGS = {
    1: {
        bg: 'linear-gradient(135deg, #facc15, #ca8a04)',
        color: MEDAL_COLORS.gold,
        shadow: 'rgba(234,179,8,0.35)',
    },
    2: {
        bg: 'linear-gradient(135deg, #d1d5db, #6b7280)',
        color: MEDAL_COLORS.silver,
        shadow: 'rgba(156,163,175,0.35)',
    },
    3: {
        bg: 'linear-gradient(135deg, #fbbf24, #b45309)',
        color: MEDAL_COLORS.bronze,
        shadow: 'rgba(217,119,6,0.35)',
    },
};

// ------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------

function getInitials(name) {
    if (!name) return '?';
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
}

function getAvatarColor(name) {
    const colors = ['#3b82f6', '#8b5cf6', '#ec4899', '#f59e0b', '#10b981', '#06b6d4', '#ef4444', '#f97316'];
    let hash = 0;
    for (let i = 0; i < (name || '').length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    return colors[Math.abs(hash) % colors.length];
}

function formatScore(score, type) {
    if (score == null) return '-';
    switch (type) {
        case 'points':
            return Number(score).toLocaleString();
        case 'courses_completed':
        case 'badges':
            return String(score);
        case 'streak':
            return `${score}d`;
        default:
            return String(score);
    }
}

function getTypeIcon(type) {
    const match = LEADERBOARD_TYPES.find((t) => t.key === type);
    return match ? match.icon : StarIcon;
}

// ------------------------------------------------------------------
// Skeleton Loaders
// ------------------------------------------------------------------

function RankingsCardSkeleton() {
    return (
        <Card
            elevation={0}
            sx={{
                borderRadius: '20px',
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                mb: 3,
            }}
        >
            <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                <Skeleton variant="text" width={140} height={28} sx={{ bgcolor: 'rgba(255,255,255,0.15)', mb: 2 }} />
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {[1, 2, 3, 4].map((i) => (
                        <Skeleton
                            key={i}
                            variant="rounded"
                            width={140}
                            height={72}
                            sx={{ bgcolor: 'rgba(255,255,255,0.12)', borderRadius: '14px' }}
                        />
                    ))}
                </Box>
            </CardContent>
        </Card>
    );
}

function PodiumSkeleton() {
    return (
        <Paper
            elevation={0}
            sx={{
                borderRadius: '20px',
                border: '1px solid #e2e8f0',
                overflow: 'hidden',
                mb: 3,
                p: 3,
                textAlign: 'center',
            }}
        >
            <Skeleton variant="text" width={160} height={30} sx={{ mx: 'auto', mb: 3 }} />
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 3 }}>
                {[150, 190, 130].map((h, i) => (
                    <Box key={i} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Skeleton variant="circular" width={i === 1 ? 80 : 64} height={i === 1 ? 80 : 64} sx={{ mb: 1.5 }} />
                        <Skeleton variant="text" width={80} height={20} sx={{ mb: 0.5 }} />
                        <Skeleton variant="text" width={50} height={18} sx={{ mb: 1 }} />
                        <Skeleton variant="rounded" width={110} height={h} sx={{ borderRadius: '16px 16px 0 0' }} />
                    </Box>
                ))}
            </Box>
        </Paper>
    );
}

function TableSkeleton() {
    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{ borderRadius: '16px', border: '1px solid #e2e8f0' }}
        >
            <Table>
                <TableHead>
                    <TableRow
                        sx={{
                            '& th': {
                                fontFamily,
                                fontWeight: 700,
                                color: '#64748b',
                                fontSize: '0.8rem',
                                bgcolor: '#f8fafc',
                                borderBottom: '2px solid #e2e8f0',
                                py: 1.5,
                            },
                        }}
                    >
                        <TableCell align="center" sx={{ width: 60 }}>Rank</TableCell>
                        <TableCell>Student</TableCell>
                        <TableCell align="center">Level</TableCell>
                        <TableCell align="center">Score</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {[...Array(8)].map((_, i) => (
                        <TableRow key={i}>
                            <TableCell align="center">
                                <Skeleton variant="circular" width={30} height={30} sx={{ mx: 'auto' }} />
                            </TableCell>
                            <TableCell>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                    <Skeleton variant="circular" width={36} height={36} />
                                    <Box>
                                        <Skeleton variant="text" width={120} height={18} />
                                        <Skeleton variant="text" width={80} height={14} />
                                    </Box>
                                </Box>
                            </TableCell>
                            <TableCell align="center">
                                <Skeleton variant="rounded" width={60} height={24} sx={{ mx: 'auto', borderRadius: '12px' }} />
                            </TableCell>
                            <TableCell align="center">
                                <Skeleton variant="text" width={50} height={20} sx={{ mx: 'auto' }} />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}

// ------------------------------------------------------------------
// Podium Card for Top 3
// ------------------------------------------------------------------

function PodiumCard({ entry, rank, type }) {
    const config = RANK_CONFIGS[rank] || RANK_CONFIGS[3];
    const name = entry.full_name || 'Student';
    const avatarUrl = entry.profile_image_url;
    const score = formatScore(entry.score, type);

    const heights = { 1: 190, 2: 155, 3: 130 };
    const avatarSizes = { 1: 80, 2: 66, 3: 58 };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                order: rank === 1 ? 1 : rank === 2 ? 0 : 2,
            }}
        >
            {/* Avatar with rank badge */}
            <Box sx={{ position: 'relative', mb: 1.5 }}>
                <Avatar
                    src={avatarUrl}
                    sx={{
                        width: avatarSizes[rank],
                        height: avatarSizes[rank],
                        fontSize: avatarSizes[rank] * 0.35,
                        fontFamily,
                        fontWeight: 700,
                        bgcolor: avatarUrl ? 'transparent' : getAvatarColor(name),
                        border: `3px solid ${config.color}`,
                        boxShadow: `0 8px 24px ${config.shadow}`,
                    }}
                >
                    {!avatarUrl && getInitials(name)}
                </Avatar>
                <Box
                    sx={{
                        position: 'absolute',
                        bottom: -8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        width: 28,
                        height: 28,
                        borderRadius: '50%',
                        background: config.bg,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 12px ${config.shadow}`,
                        border: '2px solid white',
                    }}
                >
                    <Typography sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: '0.75rem' }}>
                        {rank}
                    </Typography>
                </Box>
            </Box>

            {/* Name */}
            <Typography
                sx={{
                    fontFamily,
                    fontWeight: 700,
                    color: '#1e293b',
                    fontSize: rank === 1 ? '1rem' : '0.9rem',
                    textAlign: 'center',
                    maxWidth: 120,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    mb: 0.5,
                }}
            >
                {name}
            </Typography>

            {/* Score */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <StarIcon sx={{ fontSize: 16, color: config.color }} />
                <Typography sx={{ fontFamily, fontWeight: 700, color: config.color, fontSize: '0.95rem' }}>
                    {score}
                </Typography>
            </Box>

            {/* Podium block */}
            <Box
                sx={{
                    width: { xs: 90, sm: 120 },
                    height: heights[rank],
                    background: config.bg,
                    borderRadius: '16px 16px 0 0',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    pt: 2,
                }}
            >
                {rank === 1 && <EmojiEventsIcon sx={{ fontSize: 36, color: 'rgba(255,255,255,0.85)' }} />}
                {rank === 2 && <MilitaryTechIcon sx={{ fontSize: 32, color: 'rgba(255,255,255,0.85)' }} />}
                {rank === 3 && <WorkspacePremiumIcon sx={{ fontSize: 30, color: 'rgba(255,255,255,0.85)' }} />}
            </Box>
        </Box>
    );
}

// ------------------------------------------------------------------
// Main Component
// ------------------------------------------------------------------

export default function LeaderboardPage() {
    const [leaderboard, setLeaderboard] = useState([]);
    const [totalParticipants, setTotalParticipants] = useState(0);
    const [currentUserEntry, setCurrentUserEntry] = useState(null);
    const [currentUserRank, setCurrentUserRank] = useState(null);
    const [myRankings, setMyRankings] = useState(null);

    const [type, setType] = useState('points');
    const [timeframe, setTimeframe] = useState('all_time');

    const [loading, setLoading] = useState(true);
    const [rankingsLoading, setRankingsLoading] = useState(true);
    const [error, setError] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [disabledMessage, setDisabledMessage] = useState('');

    // ---- Fetch leaderboard data ----
    const fetchLeaderboard = useCallback(async (lbType, lbTimeframe) => {
        setLoading(true);
        setError('');
        try {
            const response = await axiosInstance.get('/api/lms/portal/leaderboard', {
                params: { type: lbType, timeframe: lbTimeframe, limit: 50 },
            });
            const data = response.data;

            if (data.disabled) {
                setDisabled(true);
                setDisabledMessage(data.message || 'The leaderboard is currently disabled.');
                setLeaderboard([]);
                setTotalParticipants(0);
                setCurrentUserEntry(null);
                setCurrentUserRank(null);
                return;
            }

            setDisabled(false);
            setDisabledMessage('');
            setLeaderboard(data.leaderboard || []);
            setTotalParticipants(data.total_participants || 0);
            setCurrentUserEntry(data.current_user_entry || null);
            setCurrentUserRank(data.current_user_rank || null);
        } catch (err) {
            console.error('Failed to fetch leaderboard:', err);
            setError(err.response?.data?.message || 'Failed to load leaderboard.');
        } finally {
            setLoading(false);
        }
    }, []);

    // ---- Fetch my rankings ----
    const fetchMyRankings = useCallback(async () => {
        setRankingsLoading(true);
        try {
            const response = await axiosInstance.get('/api/lms/portal/leaderboard/my-rankings');
            setMyRankings(response.data.rankings || null);
        } catch (err) {
            console.error('Failed to fetch my rankings:', err);
        } finally {
            setRankingsLoading(false);
        }
    }, []);

    // ---- Initial load ----
    useEffect(() => {
        fetchMyRankings();
    }, [fetchMyRankings]);

    useEffect(() => {
        fetchLeaderboard(type, timeframe);
    }, [type, timeframe, fetchLeaderboard]);

    // ---- Derived values ----
    const top3 = leaderboard.slice(0, 3);
    const rest = leaderboard.slice(3);

    // Check if current user is visible in the table
    const currentUserInList = currentUserEntry
        ? leaderboard.some((e) => e.student_id === currentUserEntry.student_id)
        : false;

    // Best rank from my rankings
    const bestRank = myRankings
        ? Math.min(
              ...[
                  myRankings.points?.rank,
                  myRankings.courses_completed?.rank,
                  myRankings.badges?.rank,
                  myRankings.streak?.rank,
              ].filter((r) => r != null && r > 0)
          )
        : null;

    // ---- Render ----
    return (
        <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc' }}>
            {/* ===== Header ===== */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    borderRadius: { xs: 0, md: '0 0 24px 24px' },
                    px: { xs: 2, md: 4 },
                    py: { xs: 3, md: 4 },
                    mb: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <TrendingUpIcon sx={{ fontSize: 40, color: 'white' }} />
                    <Box>
                        <Typography
                            variant="h4"
                            sx={{ fontFamily, fontWeight: 800, color: 'white', fontSize: { xs: '1.5rem', md: '2rem' } }}
                        >
                            Leaderboard
                        </Typography>
                        <Typography sx={{ fontFamily, color: 'rgba(255,255,255,0.85)', fontSize: '0.95rem' }}>
                            See how you rank among your peers
                        </Typography>
                    </Box>
                </Box>
            </Paper>

            <Box sx={{ px: { xs: 2, md: 4 }, pb: 4, maxWidth: 1000, mx: 'auto' }}>
                {/* ===== Error ===== */}
                {error && (
                    <Alert
                        severity="error"
                        sx={{ mb: 3, borderRadius: '12px', fontFamily }}
                        action={
                            <Button
                                color="inherit"
                                size="small"
                                onClick={() => fetchLeaderboard(type, timeframe)}
                                sx={{ fontFamily, textTransform: 'none' }}
                            >
                                Retry
                            </Button>
                        }
                    >
                        {error}
                    </Alert>
                )}

                {/* ===== Disabled State ===== */}
                {disabled && !loading && (
                    <Paper
                        sx={{
                            py: 8,
                            px: 3,
                            textAlign: 'center',
                            borderRadius: '16px',
                            border: '1px solid #e2e8f0',
                            boxShadow: 'none',
                        }}
                    >
                        <TrendingUpIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                        <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                            Leaderboard Disabled
                        </Typography>
                        <Typography sx={{ fontFamily, color: '#64748b', maxWidth: 400, mx: 'auto' }}>
                            {disabledMessage}
                        </Typography>
                    </Paper>
                )}

                {/* ===== Main content (not disabled) ===== */}
                {!disabled && (
                    <>
                        {/* ===== My Rankings Card ===== */}
                        {rankingsLoading ? (
                            <RankingsCardSkeleton />
                        ) : myRankings ? (
                            <Card
                                elevation={0}
                                sx={{
                                    borderRadius: '20px',
                                    background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                                    mb: 3,
                                    overflow: 'visible',
                                }}
                            >
                                <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5 }}>
                                        <EmojiEventsIcon sx={{ fontSize: 26, color: 'rgba(255,255,255,0.9)' }} />
                                        <Box>
                                            <Typography
                                                sx={{ fontFamily, fontWeight: 700, color: 'white', fontSize: '1.1rem' }}
                                            >
                                                My Rankings
                                            </Typography>
                                            {bestRank != null && bestRank !== Infinity && (
                                                <Typography
                                                    sx={{ fontFamily, color: 'rgba(255,255,255,0.7)', fontSize: '0.8rem' }}
                                                >
                                                    Best rank: #{bestRank}
                                                </Typography>
                                            )}
                                        </Box>
                                    </Box>

                                    <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                                        {LEADERBOARD_TYPES.map((lb) => {
                                            const Icon = lb.icon;
                                            const ranking = myRankings[lb.key];
                                            const rank = ranking?.rank;
                                            const score = ranking?.score;

                                            return (
                                                <Box
                                                    key={lb.key}
                                                    onClick={() => setType(lb.key)}
                                                    sx={{
                                                        flex: { xs: '1 1 calc(50% - 12px)', sm: '1 1 0' },
                                                        minWidth: 120,
                                                        bgcolor: type === lb.key ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)',
                                                        borderRadius: '14px',
                                                        p: 2,
                                                        cursor: 'pointer',
                                                        transition: 'all 0.2s',
                                                        border: type === lb.key ? '2px solid rgba(255,255,255,0.5)' : '2px solid transparent',
                                                        '&:hover': {
                                                            bgcolor: 'rgba(255,255,255,0.2)',
                                                        },
                                                    }}
                                                >
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <Icon sx={{ fontSize: 18, color: 'rgba(255,255,255,0.85)' }} />
                                                        <Typography
                                                            sx={{
                                                                fontFamily,
                                                                fontWeight: 600,
                                                                color: 'rgba(255,255,255,0.85)',
                                                                fontSize: '0.75rem',
                                                            }}
                                                        >
                                                            {lb.label}
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        sx={{
                                                            fontFamily,
                                                            fontWeight: 800,
                                                            color: 'white',
                                                            fontSize: '1.3rem',
                                                            lineHeight: 1.2,
                                                        }}
                                                    >
                                                        {rank != null ? `#${rank}` : '--'}
                                                    </Typography>
                                                    {score != null && (
                                                        <Typography
                                                            sx={{
                                                                fontFamily,
                                                                color: 'rgba(255,255,255,0.65)',
                                                                fontSize: '0.72rem',
                                                                mt: 0.3,
                                                            }}
                                                        >
                                                            {formatScore(score, lb.key)}
                                                        </Typography>
                                                    )}
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </CardContent>
                            </Card>
                        ) : null}

                        {/* ===== Type & Timeframe Selectors ===== */}
                        <Paper
                            elevation={0}
                            sx={{
                                borderRadius: '16px',
                                border: '1px solid #e2e8f0',
                                p: 2.5,
                                mb: 3,
                            }}
                        >
                            {/* Type buttons */}
                            <Box sx={{ mb: 2 }}>
                                <Typography
                                    sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.8rem', mb: 1.5 }}
                                >
                                    Type
                                </Typography>
                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                    {LEADERBOARD_TYPES.map((lb) => {
                                        const Icon = lb.icon;
                                        const isActive = type === lb.key;
                                        return (
                                            <Button
                                                key={lb.key}
                                                variant={isActive ? 'contained' : 'outlined'}
                                                startIcon={<Icon sx={{ fontSize: 18 }} />}
                                                onClick={() => setType(lb.key)}
                                                sx={{
                                                    fontFamily,
                                                    fontWeight: 600,
                                                    fontSize: '0.8rem',
                                                    textTransform: 'none',
                                                    borderRadius: '10px',
                                                    px: 2,
                                                    py: 0.8,
                                                    bgcolor: isActive ? ACCENT : 'transparent',
                                                    color: isActive ? 'white' : '#475569',
                                                    borderColor: isActive ? ACCENT : '#e2e8f0',
                                                    boxShadow: isActive ? `0 4px 14px ${ACCENT}40` : 'none',
                                                    '&:hover': {
                                                        bgcolor: isActive ? '#7c3aed' : '#f8fafc',
                                                        borderColor: isActive ? '#7c3aed' : '#cbd5e1',
                                                    },
                                                }}
                                            >
                                                {lb.label}
                                            </Button>
                                        );
                                    })}
                                </Box>
                            </Box>

                            {/* Timeframe dropdown */}
                            <Box>
                                <Typography
                                    sx={{ fontFamily, fontWeight: 600, color: '#64748b', fontSize: '0.8rem', mb: 1.5 }}
                                >
                                    Timeframe
                                </Typography>
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                    <Select
                                        value={timeframe}
                                        onChange={(e) => setTimeframe(e.target.value)}
                                        sx={{
                                            fontFamily,
                                            fontWeight: 600,
                                            fontSize: '0.85rem',
                                            borderRadius: '10px',
                                            '& .MuiOutlinedInput-notchedOutline': {
                                                borderColor: '#e2e8f0',
                                            },
                                            '&:hover .MuiOutlinedInput-notchedOutline': {
                                                borderColor: ACCENT,
                                            },
                                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                                borderColor: ACCENT,
                                            },
                                        }}
                                    >
                                        {TIMEFRAMES.map((tf) => (
                                            <MenuItem
                                                key={tf.key}
                                                value={tf.key}
                                                sx={{ fontFamily, fontWeight: 500, fontSize: '0.85rem' }}
                                            >
                                                {tf.label}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Box>
                        </Paper>

                        {/* ===== Leaderboard Info ===== */}
                        {!loading && leaderboard.length > 0 && (
                            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
                                <Typography sx={{ fontFamily, color: '#64748b', fontSize: '0.88rem' }}>
                                    Showing top performers ranked by{' '}
                                    <Box component="span" sx={{ fontWeight: 700, color: ACCENT }}>
                                        {LEADERBOARD_TYPES.find((t) => t.key === type)?.label || type}
                                    </Box>
                                    {' '}&mdash;{' '}
                                    <Box component="span" sx={{ fontWeight: 600, color: '#475569' }}>
                                        {TIMEFRAMES.find((t) => t.key === timeframe)?.label || timeframe}
                                    </Box>
                                </Typography>
                                <Chip
                                    icon={<PersonIcon sx={{ fontSize: 16 }} />}
                                    label={`${totalParticipants.toLocaleString()} participant${totalParticipants !== 1 ? 's' : ''}`}
                                    size="small"
                                    sx={{
                                        fontFamily,
                                        fontWeight: 600,
                                        fontSize: '0.78rem',
                                        bgcolor: `${ACCENT}12`,
                                        color: ACCENT,
                                        '& .MuiChip-icon': { color: ACCENT },
                                    }}
                                />
                            </Box>
                        )}

                        {/* ===== Loading skeletons ===== */}
                        {loading ? (
                            <>
                                <PodiumSkeleton />
                                <TableSkeleton />
                            </>
                        ) : leaderboard.length === 0 && !error ? (
                            <Paper
                                sx={{
                                    py: 8,
                                    px: 3,
                                    textAlign: 'center',
                                    borderRadius: '16px',
                                    border: '1px solid #e2e8f0',
                                    boxShadow: 'none',
                                }}
                            >
                                <TrendingUpIcon sx={{ fontSize: 64, color: '#cbd5e1', mb: 2 }} />
                                <Typography variant="h6" sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 1 }}>
                                    No leaderboard data yet
                                </Typography>
                                <Typography sx={{ fontFamily, color: '#64748b' }}>
                                    Start earning points to appear on the leaderboard!
                                </Typography>
                            </Paper>
                        ) : (
                            <>
                                {/* ===== Top 3 Podium ===== */}
                                {top3.length > 0 && (
                                    <Paper
                                        elevation={0}
                                        sx={{
                                            borderRadius: '20px',
                                            border: '1px solid #e2e8f0',
                                            overflow: 'hidden',
                                            mb: 3,
                                            pb: 0,
                                        }}
                                    >
                                        <Box sx={{ p: 3, textAlign: 'center' }}>
                                            <Typography
                                                variant="h6"
                                                sx={{ fontFamily, fontWeight: 700, color: '#1e293b', mb: 3 }}
                                            >
                                                Top Performers
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'flex-end',
                                                    gap: { xs: 1, sm: 3 },
                                                }}
                                            >
                                                {top3.length >= 2 && (
                                                    <PodiumCard entry={top3[1]} rank={2} type={type} />
                                                )}
                                                {top3.length >= 1 && (
                                                    <PodiumCard entry={top3[0]} rank={1} type={type} />
                                                )}
                                                {top3.length >= 3 && (
                                                    <PodiumCard entry={top3[2]} rank={3} type={type} />
                                                )}
                                            </Box>
                                        </Box>
                                    </Paper>
                                )}

                                {/* ===== Leaderboard Table ===== */}
                                <TableContainer
                                    component={Paper}
                                    elevation={0}
                                    sx={{
                                        borderRadius: '16px',
                                        border: '1px solid #e2e8f0',
                                        mb: 3,
                                    }}
                                >
                                    <Table>
                                        <TableHead>
                                            <TableRow
                                                sx={{
                                                    '& th': {
                                                        fontFamily,
                                                        fontWeight: 700,
                                                        color: '#64748b',
                                                        fontSize: '0.8rem',
                                                        bgcolor: '#f8fafc',
                                                        borderBottom: '2px solid #e2e8f0',
                                                        py: 1.5,
                                                    },
                                                }}
                                            >
                                                <TableCell align="center" sx={{ width: 60 }}>
                                                    Rank
                                                </TableCell>
                                                <TableCell>Student</TableCell>
                                                <TableCell
                                                    align="center"
                                                    sx={{ display: { xs: 'none', sm: 'table-cell' } }}
                                                >
                                                    Level
                                                </TableCell>
                                                <TableCell align="center">Score</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {leaderboard.map((entry, index) => {
                                                const rank = entry.rank || index + 1;
                                                const name = entry.full_name || 'Student';
                                                const avatarUrl = entry.profile_image_url;
                                                const department = entry.department;
                                                const level = entry.current_level;
                                                const score = formatScore(entry.score, type);
                                                const isCurrent = !!entry.is_current_user;
                                                const rankConfig = RANK_CONFIGS[rank];

                                                return (
                                                    <TableRow
                                                        key={entry.student_id || index}
                                                        sx={{
                                                            bgcolor: isCurrent ? `${ACCENT}0A` : 'transparent',
                                                            borderLeft: isCurrent
                                                                ? `4px solid ${ACCENT}`
                                                                : '4px solid transparent',
                                                            '& td': {
                                                                fontFamily,
                                                                py: 1.5,
                                                                borderBottom: '1px solid #f1f5f9',
                                                            },
                                                            '&:hover': {
                                                                bgcolor: isCurrent ? `${ACCENT}14` : '#f8fafc',
                                                            },
                                                            transition: 'background-color 0.2s',
                                                        }}
                                                    >
                                                        {/* Rank */}
                                                        <TableCell align="center">
                                                            {rankConfig ? (
                                                                <Box
                                                                    sx={{
                                                                        width: 30,
                                                                        height: 30,
                                                                        borderRadius: '50%',
                                                                        background: rankConfig.bg,
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        justifyContent: 'center',
                                                                        mx: 'auto',
                                                                    }}
                                                                >
                                                                    <Typography
                                                                        sx={{
                                                                            fontFamily,
                                                                            fontWeight: 800,
                                                                            color: 'white',
                                                                            fontSize: '0.8rem',
                                                                        }}
                                                                    >
                                                                        {rank}
                                                                    </Typography>
                                                                </Box>
                                                            ) : (
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily,
                                                                        fontWeight: 600,
                                                                        color: '#64748b',
                                                                    }}
                                                                >
                                                                    {rank}
                                                                </Typography>
                                                            )}
                                                        </TableCell>

                                                        {/* Student */}
                                                        <TableCell>
                                                            <Box
                                                                sx={{
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    gap: 1.5,
                                                                }}
                                                            >
                                                                <Avatar
                                                                    src={avatarUrl}
                                                                    sx={{
                                                                        width: 36,
                                                                        height: 36,
                                                                        fontSize: '0.85rem',
                                                                        fontFamily,
                                                                        fontWeight: 700,
                                                                        bgcolor: avatarUrl
                                                                            ? 'transparent'
                                                                            : getAvatarColor(name),
                                                                    }}
                                                                >
                                                                    {!avatarUrl && getInitials(name)}
                                                                </Avatar>
                                                                <Box>
                                                                    <Box
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 0.75,
                                                                        }}
                                                                    >
                                                                        <Typography
                                                                            sx={{
                                                                                fontFamily,
                                                                                fontWeight: 600,
                                                                                color: '#1e293b',
                                                                                fontSize: '0.88rem',
                                                                            }}
                                                                        >
                                                                            {name}
                                                                        </Typography>
                                                                        {isCurrent && (
                                                                            <Chip
                                                                                label="You"
                                                                                size="small"
                                                                                sx={{
                                                                                    fontFamily,
                                                                                    fontWeight: 700,
                                                                                    fontSize: '0.6rem',
                                                                                    height: 20,
                                                                                    bgcolor: `${ACCENT}18`,
                                                                                    color: ACCENT,
                                                                                }}
                                                                            />
                                                                        )}
                                                                    </Box>
                                                                    {department && (
                                                                        <Typography
                                                                            sx={{
                                                                                fontFamily,
                                                                                color: '#94a3b8',
                                                                                fontSize: '0.75rem',
                                                                            }}
                                                                        >
                                                                            {department}
                                                                        </Typography>
                                                                    )}
                                                                </Box>
                                                            </Box>
                                                        </TableCell>

                                                        {/* Level */}
                                                        <TableCell
                                                            align="center"
                                                            sx={{
                                                                display: { xs: 'none', sm: 'table-cell' },
                                                            }}
                                                        >
                                                            {level ? (
                                                                <Chip
                                                                    label={level}
                                                                    size="small"
                                                                    sx={{
                                                                        fontFamily,
                                                                        fontWeight: 600,
                                                                        fontSize: '0.7rem',
                                                                        bgcolor: '#f1f5f9',
                                                                        color: '#475569',
                                                                        height: 24,
                                                                    }}
                                                                />
                                                            ) : (
                                                                <Typography
                                                                    sx={{
                                                                        fontFamily,
                                                                        color: '#cbd5e1',
                                                                        fontSize: '0.8rem',
                                                                    }}
                                                                >
                                                                    -
                                                                </Typography>
                                                            )}
                                                        </TableCell>

                                                        {/* Score */}
                                                        <TableCell align="center">
                                                            <Typography
                                                                sx={{
                                                                    fontFamily,
                                                                    fontWeight: 700,
                                                                    color: '#1e293b',
                                                                    fontSize: '0.9rem',
                                                                }}
                                                            >
                                                                {score}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* ===== Current User Position Card (if not in visible list) ===== */}
                                {currentUserEntry && !currentUserInList && (
                                    <Card
                                        elevation={0}
                                        sx={{
                                            borderRadius: '16px',
                                            border: `2px solid ${ACCENT}`,
                                            bgcolor: `${ACCENT}08`,
                                            mb: 3,
                                        }}
                                    >
                                        <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                                            <Typography
                                                sx={{
                                                    fontFamily,
                                                    fontWeight: 600,
                                                    color: '#64748b',
                                                    fontSize: '0.75rem',
                                                    textTransform: 'uppercase',
                                                    letterSpacing: '0.05em',
                                                    mb: 1.5,
                                                }}
                                            >
                                                Your Position
                                            </Typography>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 2,
                                                }}
                                            >
                                                {/* Rank */}
                                                <Box
                                                    sx={{
                                                        width: 44,
                                                        height: 44,
                                                        borderRadius: '12px',
                                                        bgcolor: ACCENT,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                    }}
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontFamily,
                                                            fontWeight: 800,
                                                            color: 'white',
                                                            fontSize: '1rem',
                                                        }}
                                                    >
                                                        #{currentUserEntry.rank || currentUserRank || '?'}
                                                    </Typography>
                                                </Box>

                                                {/* Avatar + Name */}
                                                <Avatar
                                                    src={currentUserEntry.profile_image_url}
                                                    sx={{
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: '0.9rem',
                                                        fontFamily,
                                                        fontWeight: 700,
                                                        bgcolor: currentUserEntry.profile_image_url
                                                            ? 'transparent'
                                                            : getAvatarColor(currentUserEntry.full_name),
                                                    }}
                                                >
                                                    {!currentUserEntry.profile_image_url &&
                                                        getInitials(currentUserEntry.full_name)}
                                                </Avatar>
                                                <Box sx={{ flex: 1 }}>
                                                    <Typography
                                                        sx={{
                                                            fontFamily,
                                                            fontWeight: 700,
                                                            color: '#1e293b',
                                                            fontSize: '0.95rem',
                                                        }}
                                                    >
                                                        {currentUserEntry.full_name || 'You'}
                                                    </Typography>
                                                    {currentUserEntry.department && (
                                                        <Typography
                                                            sx={{
                                                                fontFamily,
                                                                color: '#94a3b8',
                                                                fontSize: '0.75rem',
                                                            }}
                                                        >
                                                            {currentUserEntry.department}
                                                        </Typography>
                                                    )}
                                                </Box>

                                                {/* Level */}
                                                {currentUserEntry.current_level && (
                                                    <Chip
                                                        label={currentUserEntry.current_level}
                                                        size="small"
                                                        sx={{
                                                            fontFamily,
                                                            fontWeight: 600,
                                                            fontSize: '0.7rem',
                                                            bgcolor: '#f1f5f9',
                                                            color: '#475569',
                                                            height: 24,
                                                            display: { xs: 'none', sm: 'flex' },
                                                        }}
                                                    />
                                                )}

                                                {/* Score */}
                                                <Typography
                                                    sx={{
                                                        fontFamily,
                                                        fontWeight: 800,
                                                        color: ACCENT,
                                                        fontSize: '1.1rem',
                                                    }}
                                                >
                                                    {formatScore(currentUserEntry.score, type)}
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                )}
                            </>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
}
